"use client";

import React from "react";
import { Mail, MapPin, Phone, Sun, Moon } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { useLocation } from "react-router-dom";
function FooterMenuLinks() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-24 animate-pulse rounded bg-muted/20" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackLinks = [{
      label: "Home",
      to: "/HomePage"
    }, {
      label: "Packages",
      to: "/PackagesPage"
    }, {
      label: "Portfolio",
      to: "/PortfolioPage"
    }, {
      label: "About",
      to: "/About"
    }, {
      label: "Contact",
      to: "/ContactPage"
    }];
    return <nav className="flex flex-col gap-3">
        {fallbackLinks.map((link, i) => <Link key={i} to={link.to} className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium w-fit" data-index={i}>
            {link.label}
          </Link>)}
      </nav>;
  }
  return <nav className="flex flex-col gap-3">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium w-fit" data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function Footer() {
  const {
    pathname
  } = useLocation();
  const isReferralPage = pathname === "/ReferralProgramLandingPage";
  const currentYear = new Date().getFullYear();
  const [theme, setTheme] = React.useState<"light" | "dark">("dark");
  React.useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
    const initialTheme = savedTheme || "dark";
    setTheme(initialTheme);
    document.documentElement.classList.toggle("dark", initialTheme === "dark");
  }, []);
  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
  };
  return <footer id="footer" className="bg-background text-foreground pt-16 pb-8 border-t border-border/10 transition-colors duration-300">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          <div className="flex flex-col gap-6">
            <Link to="/HomePage" className="block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569073/img/website_shop_logo_footer.png" alt="Website Shop Logo" className="h-7 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              Website Shop builds affordable, professional websites for small businesses and trades across Australia. 
              We help you attract more customers with fast, modern designs.
            </p>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-semibold tracking-tight text-foreground">Company</h3>
            <MenuProvider menu_id="17" className="w-full">
              <FooterMenuLinks />
            </MenuProvider>
            
            {!isReferralPage && <div className="fixed bottom-6 right-6 z-[100] md:hidden">
                <button onClick={toggleTheme} className="flex items-center gap-3 px-5 py-2.5 rounded-full bg-secondary text-secondary-foreground border border-border/10 shadow-lg hover:bg-secondary/80 transition-all duration-300" aria-label={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}>
                  {theme === "dark" ? <Sun className="h-4 w-4 text-primary" /> : <Moon className="h-4 w-4 text-primary" />}
                  <span className="text-sm font-medium">
                    {theme === "dark" ? "Light Mode" : "Dark Mode"}
                  </span>
                </button>
              </div>}
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-semibold tracking-tight text-foreground">Contact</h3>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3 text-muted-foreground group">
                <Phone className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm group-hover:text-foreground transition-colors">
                  0473 462 554
                </span>
              </div>
              <div className="flex items-start gap-3 text-muted-foreground group">
                <Mail className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm group-hover:text-foreground transition-colors">
                  Admin@websiteshop.com.au
                </span>
              </div>
              <div className="flex items-start gap-3 text-muted-foreground group">
                <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm group-hover:text-foreground transition-colors">
                  Central Coast, Australia
                </span>
              </div>
            </div>
          </div>

          <div className="hidden lg:flex flex-col gap-6" />
          
        </div>

        <Separator className="bg-border/20 mb-8" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground/60">
          <p>
            &copy; {currentYear} Website Shop. All rights reserved.
          </p>
          <div className="flex items-center gap-1">
            <span>Built by</span>
            <Link to="/HomePage" className="hover:text-primary transition-colors font-medium">
              Website Shop
            </Link>
          </div>
        </div>
      </div>

      {!isReferralPage && <div className="fixed bottom-10 right-10 z-[100] hidden md:block">
          <button onClick={toggleTheme} className="flex items-center gap-3 px-8 py-4 rounded-full bg-primary text-primary-foreground shadow-[0_0_20px_rgba(61,193,188,0.3)] hover:scale-105 active:scale-95 transition-all duration-300 group" aria-label={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}>
            {theme === "dark" ? <Sun className="h-5 w-5 transition-transform group-hover:rotate-45" /> : <Moon className="h-5 w-5 transition-transform group-hover:-rotate-12" />}
            <span className="text-sm font-bold uppercase tracking-widest Montserrat">
              {theme === "dark" ? "Light Mode" : "Dark Mode"}
            </span>
          </button>
        </div>}
    </footer>;
}